<template>
  <div class="hd-reprocessing-modal">
    <form
      class="edit-form needs-validation"
      @submit.prevent="updateReprocessingOptions"
    >
      <div class="modal-content-block">
        <h4 v-if="action === PresetAction.NEW_PRESET">
          {{ $t("uploader.form.new_preset_name") }}
        </h4>
        <div
          class="form-group"
          :class="{
            'form-group--error': $v.name.$error
          }"
        >
          <input
            id="presetName"
            v-model.trim="$v.name.$model"
            type="text"
            class="form-control"
            :readonly="action === PresetAction.UPDATE_PRESET"
            :class="{ 'is-invalid': $v.name.$error }"
          />
          <div class="invalid-feedback" v-if="!$v.name.required">
            Field is required
          </div>
          <div class="invalid-feedback" v-if="!$v.name.minLength">
            Name must have at least {{ $v.name.$params.minLength.min }} letters.
          </div>
        </div>
      </div>
      <div class="modal-content-block">
        <h3>{{ $t("storage.modal.basic_options") }}</h3>
        <div class="form-group">
          <label>
            {{ $t("storage.modal.quality_title") }}
            <span
              v-tooltip.auto="$t('storage.options.quality_tooltip')"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </label>
          <div class="form-row">
            <p-radio v-model="options.quality" :value="quality.FULL">{{
              $t("storage.options.quality.full")
            }}</p-radio>
          </div>
          <div class="form-row">
            <p-radio v-model="options.quality" :value="quality.FAST">{{
              $t("storage.options.quality.fast")
            }}</p-radio>
          </div>
        </div>
        <div class="form-group">
          <label for="userscenario"
            >{{ $t("storage.modal.userscenario_title") }}
            <span
              v-tooltip.auto="$t('storage.options.scenario_tooltip')"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </label>
          <multiselect
            id="userscenario"
            v-model="options.scenario"
            :options="scenarios"
            :allow-empty="false"
            deselect-label=""
            :customLabel="label => $t(`storage.options.scenario.${label}`)"
          />

          <i18n path="storage.modal.scenario_info" tag="p" class="mt-5">
            <a :href="$t('storage.modal.scenario_info_url')" target="_blank">{{
              $t("storage.modal.scenario_info_link")
            }}</a>
          </i18n>
        </div>
      </div>
      <div class="modal-content-block">
        <h3>{{ $t("storage.modal.options_title") }}</h3>
        <div class="form-group">
          <div class="form-row">
            <p-check
              v-model="options.positionaiding"
              :true-value="1"
              :false-value="0"
            >
              {{ $t("storage.options.position_aiding") }}
            </p-check>
            <span
              v-tooltip.auto="{
                content: $t('storage.options.position_aiding_tooltip')
              }"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </div>
        </div>
        <div class="form-group">
          <div class="form-row">
            <p-check
              v-model="options.resetposition"
              :true-value="1"
              :false-value="0"
              :disabled="options.positionaiding"
              >{{ $t("storage.options.reset_position") }}
            </p-check>
            <span
              v-tooltip.auto="{
                content: $t('storage.options.reset_position_tooltip')
              }"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </div>
        </div>
        <div class="form-group">
          <div class="form-row">
            <p-check
              v-model="options.resetheading"
              :true-value="1"
              :false-value="0"
              :disabled="options.positionaiding"
              >{{ $t("storage.options.reset_heading") }}</p-check
            >
            <span
              v-tooltip.auto="{
                content: $t('storage.options.reset_heading_tooltip')
              }"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </div>
        </div>
        <div class="form-group">
          <div class="form-row">
            <p-check
              v-model="options.resetengine"
              :true-value="1"
              :false-value="0"
              >{{ $t("storage.options.reset_engine") }}</p-check
            >
            <span
              v-tooltip.auto="{
                content: $t('storage.options.reset_engine_tooltip')
              }"
              class="form-tooltip"
            >
              <svg class="icon icon-xsens_8_tooltip">
                <use xlink:href="#icon-xsens_8_tooltip" />
              </svg>
            </span>
          </div>
        </div>
        <div class="modal-content-block">
          <div class="form-group cta-form-group">
            <button
              type="submit"
              class="btn btn-block btn-primary"
              :disabled="
                (preset.action === PresetAction.NEW_PRESET &&
                  ($v.$invalid || !$v.$dirty)) ||
                  (preset.action === PresetAction.NEW_PRESET && $v.$invalid)
              "
            >
              {{ $t("storage.modal.save_btn") }}
            </button>
          </div>
          <div class="form-group">
            <button
              type="button"
              class="btn btn-block btn-outline-secondary"
              @click="$emit('cancel')"
            >
              {{ $t("storage.modal.cancel_btn") }}
            </button>
          </div>
          <div
            class="form-group"
            v-if="preset.action !== PresetAction.NEW_PRESET"
          >
            <button
              type="button"
              class="btn btn-block btn-outline-danger"
              @click="$emit('delete')"
            >
              {{ $t("storage.modal.delete_btn") }}
            </button>
          </div>
        </div>

        <div class="modal-content-block">
          <i18n path="storage.modal.hd_processing_info" tag="p" class="mt-5">
            <a
              :href="$t('storage.modal.hd_processing_info_url')"
              target="_blank"
              >{{ $t("storage.modal.hd_processing_info_link") }}</a
            >
          </i18n>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";
import {
  PresetItem,
  PresetAction,
  Quality,
  ReprocessingOptions,
  Scenario,
  ResetEngine,
  ResetHeading,
  ResetPosition,
  PositionAiding,
  NoReset
} from "@xsenscloud/xse-styleguide";

export const REPROCESSING_DEFAULTS = {
  quality: Quality.FULL,
  scenario: Scenario.SINGLE_LEVEL,
  resetengine: ResetEngine.OFF,
  resetheading: ResetHeading.OFF,
  resetposition: ResetPosition.OFF,
  positionaiding: PositionAiding.OFF,
  noreset: NoReset.OFF
};

@Component({
  validations: {
    name: {
      required,
      minLength: minLength(4)
    }
  }
})
export default class ReprocessingOptionsForm extends mixins(validationMixin) {
  @Prop()
  action!: PresetAction;

  @Prop()
  preset!: PresetItem;

  options: ReprocessingOptions = {
    ...REPROCESSING_DEFAULTS
  };

  PresetAction = PresetAction;

  name = "";

  created() {
    this.setOptions();
  }

  @Watch("preset")
  onPresetChanged() {
    this.setOptions();
  }

  setOptions() {
    if (this.preset) this.name = this.preset.name;

    if (this.preset.value) {
      this.options = {
        ...(this.preset.value as ReprocessingOptions)
      };
    }
  }

  scenarios: Array<Scenario> = [
    Scenario.SINGLE_LEVEL,
    Scenario.MULTI_LEVEL,
    Scenario.NO_LEVEL,
    Scenario.SOFT_FLOOR,
    Scenario.VEHICLE
  ];

  quality = Quality;

  updateReprocessingOptions() {
    this.$v.$touch();
    if (this.$v.$invalid) {
      return;
    }
    this.$emit("save-preset", {
      name: this.name,
      value: this.options
    });
  }
}
</script>
